import { Injectable } from '@angular/core';
import { BehaviorSubject, from, fromEvent, interval, merge, Observable, Subscription } from 'rxjs';
import { filter, startWith, switchMap } from 'rxjs/operators';

/** Milliseconds between background checks for a new deployed version. */
export const VERSION_CHECK_POLL_MS = 10 * 60 * 1000;

/** Shape of the build-time stamp written by `scripts/write-version-stamp.mjs`. */
export interface VersionStamp {
  version: string;
  builtAt: string;
}

/**
 * Detects that a new BinView build has been deployed while this tab has been open, by polling
 * the build-time `version.json` stamp, and exposes reactive state so `VersionCheckBannerComponent`
 * can prompt the user to reload. Uses the native `fetch` API rather than Angular's `HttpClient`
 * so these background checks never pass through `NetworkInterceptor` and flash the app's global
 * loading spinner for something the user never asked for.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private baselineVersion: string | null = null;
  private dismissedVersion: string | null = null;
  private subscription: Subscription | null = null;

  private notifiedVersionSubject = new BehaviorSubject<string | null>(null);

  /** Emits the newly detected version when the user should be prompted to reload, or null when there's nothing to show. */
  // Deliberately not readonly: jasmine.SpyObj<VersionCheckService> preserves readonly through
  // its type, which would break the test-double assignments in app.component.spec.ts and
  // version-check-banner.component.spec.ts.
  notifiedVersion$: Observable<string | null> = this.notifiedVersionSubject.asObservable();

  /** Overridable in tests; reloads the page when the user accepts the "Reload" prompt. */
  reload: () => void = () => window.location.reload();

  /**
   * Begins polling `/version.json` on an interval and whenever the tab regains
   * visibility. Safe to call multiple times — only the first call starts polling.
   */
  start(): void {
    if (this.subscription) {
      return;
    }

    const tick$ = merge(
      interval(VERSION_CHECK_POLL_MS),
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'))
    ).pipe(startWith(0));

    this.subscription = tick$.pipe(
      switchMap(() => from(this.fetchVersion()))
    ).subscribe(stamp => this.handleStamp(stamp));
  }

  private async fetchVersion(): Promise<VersionStamp | null> {
    try {
      const response = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) {
        return null;
      }
      return await response.json() as VersionStamp;
    } catch (err) {
      console.warn('VersionCheckService: failed to fetch version.json', err);
      return null;
    }
  }

  private handleStamp(stamp: VersionStamp | null): void {
    if (!stamp) {
      return;
    }
    if (this.baselineVersion === null) {
      this.baselineVersion = stamp.version;
      return;
    }
    const alreadyNotified = stamp.version === this.notifiedVersionSubject.value;
    const alreadyDismissed = stamp.version === this.dismissedVersion;
    if (stamp.version !== this.baselineVersion && !alreadyNotified && !alreadyDismissed) {
      this.notifiedVersionSubject.next(stamp.version);
    }
  }

  /**
   * Dismisses the current prompt without reloading. The dismissed version won't reappear on a
   * later poll, but a genuinely different new version detected afterward still will.
   */
  dismiss(): void {
    const current = this.notifiedVersionSubject.value;
    if (current === null) {
      return;
    }
    this.dismissedVersion = current;
    this.notifiedVersionSubject.next(null);
  }

  /**
   * Stops polling. Used by tests to tear down the subscription between specs; not called in
   * production, since the service polls for the tab's lifetime.
   */
  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }
}
